import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import campaignService from '../services/campaignService';
import { useNavigate } from 'react-router-dom'; 
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Hook for fetching all campaigns
export function useCampaigns() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Create a user-specific query key
  const queryKey = user?.id ? ['campaigns', user.id] : ['campaigns', 'anonymous'];
  
  const result = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        return await campaignService.getCampaigns();
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        
        // If unauthorized, clear token and redirect to login
        if (error?.response?.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Authentication required. Please log in.');
        }
        
        throw new Error(error.message || 'Failed to fetch campaigns');
      }
    },
    retry: (failureCount, error) => {
      // Don't retry for authentication errors
      if (error?.message?.includes('Authentication required')) {
        return false;
      }
      return failureCount < 2; // Retry 2 times for other errors
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check for authentication error and redirect to login
  useEffect(() => {
    if (result.error?.message?.includes('Authentication required')) {
      navigate('/login');
    }
  }, [result.error, navigate]);

  return result;
}

// Hook for fetching a single campaign (for backward compatibility)
export function useCampaign(id, isValidId = true) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      if (!id || !isValidId) {
        throw new Error('Invalid campaign ID');
      }
      
      try {
        // Use default offset, limit, and tab for backward compatibility
        const data = await campaignService.getCampaign(id, 0, 50, 'companies');
        if (!data) {
          throw new Error('Campaign not found');
        }
        return data;
      } catch (error) {
        console.error(`Error fetching campaign ${id}:`, error);
        throw new Error(error.message || 'Failed to fetch campaign details');
      }
    },
    enabled: !!id && isValidId, // Only run the query if id is valid
    retry: 1, // Minimal retry on errors
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook for deleting a campaign
export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = user?.id ? ['campaigns', user.id] : ['campaigns', 'anonymous'];
  
  return useMutation({
    mutationFn: async (id) => {
      if (!id) {
        throw new Error('Invalid campaign ID for deletion');
      }
      
      try {
        return await campaignService.deleteCampaign(id);
      } catch (error) {
        console.error(`Error deleting campaign ${id}:`, error);
        throw new Error(error.message || 'Failed to delete campaign');
      }
    },
    onSuccess: () => {
      // Invalidate the user-specific campaigns query
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: (error) => {
      console.error('Mutation error in useDeleteCampaign:', error);
    }
  });
}

// Hook for renaming a campaign
export function useRenameCampaign() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = user?.id ? ['campaigns', user.id] : ['campaigns', 'anonymous'];

  return useMutation({
    mutationFn: async ({ id, name }) => {
      if (!id) {
        throw new Error('Invalid campaign ID for update');
      }
      if (!name?.trim()) {
        throw new Error('Campaign name cannot be empty');
      }
      return campaignService.updateCampaign(id, { name: name.trim() });
    },
    onMutate: async ({ id, name }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });

      const previousCampaigns = queryClient.getQueryData(queryKey);

      if (previousCampaigns) {
        queryClient.setQueryData(queryKey, (old) => {
          if (!old) {
            return old;
          }
          return old.map((campaign) => {
            const campaignId = campaign?.id || campaign?._id;
            if (campaignId === id) {
              return {
                ...campaign,
                name: name.trim(),
              };
            }
            return campaign;
          });
        });
      }

      return { previousCampaigns };
    },
    onError: (error, _variables, context) => {
      if (context?.previousCampaigns) {
        queryClient.setQueryData(queryKey, context.previousCampaigns);
      }
      console.error('Mutation error in useRenameCampaign:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });
}